var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a;
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { deleteProduct } from "../../api/productService";
import { ErrorType } from "../../types/ErrorType";
import { getErrorCode } from "../../utils/getErrorCode";
import { CO_EDITION_REQUESTS_QUERY_KEY } from "../useCoEditionRequestsData";
var DELETE_ERROR_MESSAGE_IDS = (_a = {},
    _a[ErrorType.PRODUCT_HAS_NUMBER] = "ui-br-agency.product.delete.hasNumber",
    _a[ErrorType.PRODUCT_NOT_OWNED_BY_GUARANTOR] = "ui-br-agency.product.delete.notAllowed",
    _a[ErrorType.PRODUCT_DELETE_NOT_ALLOWED] = "ui-br-agency.product.delete.notAllowed",
    _a);
var GENERIC_DELETE_ERROR_ID = "ui-br-agency.product.error.delete";
/** Maps a backend error code of a failed product delete to a translation id. */
export var resolveProductDeleteErrorMessageId = function (code) { var _a; return (_a = (code && DELETE_ERROR_MESSAGE_IDS[code])) !== null && _a !== void 0 ? _a : GENERIC_DELETE_ERROR_ID; };
var numberLabel = function (number) {
    return number.fullFormattedValue || number.formattedValue || number.value || "";
};
/**
 * Confirm + delete flow for a product, shared by the product list detail pane
 * and the standalone product detail page. For a product with number(s) the
 * confirmation warns that the numbers will be retired.
 */
export var useProductDelete = function (_a) {
    var onDeleted = _a.onDeleted;
    var ky = useOkapiKy();
    var formatMessage = useIntl().formatMessage;
    var queryClient = useQueryClient();
    var _b = useState(null), pendingProductId = _b[0], setPendingProductId = _b[1];
    var _c = useState(""), confirmMessage = _c[0], setConfirmMessage = _c[1];
    var _d = useState(false), isDeleting = _d[0], setIsDeleting = _d[1];
    var _e = useState(null), errorMessage = _e[0], setErrorMessage = _e[1];
    var openDeleteConfirm = function (product, productNumbers) {
        var _a, _b, _c, _d;
        if (productNumbers === void 0) { productNumbers = []; }
        var name = ((_c = (_b = (_a = product.title) === null || _a === void 0 ? void 0 : _a.names) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.name) || product.id || "";
        var numbers = productNumbers.map(numberLabel).filter(Boolean).join(", ");
        setConfirmMessage(productNumbers.length > 0
            ? formatMessage({ id: "ui-br-agency.product.delete.confirm.withNumber" }, { name: name, numbers: numbers })
            : formatMessage({ id: "ui-br-agency.product.delete.confirm" }, { name: name }));
        setPendingProductId((_d = product.id) !== null && _d !== void 0 ? _d : null);
    };
    var cancelDelete = function () { return setPendingProductId(null); };
    var confirmDelete = function () { return __awaiter(void 0, void 0, void 0, function () {
        var productId, error_1, code;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!pendingProductId)
                        return [2 /*return*/];
                    productId = pendingProductId;
                    setIsDeleting(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, deleteProduct(ky, productId)];
                case 2:
                    _a.sent();
                    setPendingProductId(null);
                    queryClient.removeQueries(["product", productId]);
                    queryClient.invalidateQueries("products");
                    queryClient.invalidateQueries("productNumbers");
                    queryClient.invalidateQueries(CO_EDITION_REQUESTS_QUERY_KEY);
                    queryClient.invalidateQueries("relatedProducts");
                    onDeleted();
                    return [3 /*break*/, 6];
                case 3:
                    error_1 = _a.sent();
                    return [4 /*yield*/, getErrorCode(error_1)];
                case 4:
                    code = _a.sent();
                    setPendingProductId(null);
                    setErrorMessage(formatMessage({ id: resolveProductDeleteErrorMessageId(code) }));
                    return [3 /*break*/, 6];
                case 5:
                    setIsDeleting(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    return {
        isConfirmOpen: !!pendingProductId,
        confirmMessage: confirmMessage,
        isDeleting: isDeleting,
        openDeleteConfirm: openDeleteConfirm,
        confirmDelete: confirmDelete,
        cancelDelete: cancelDelete,
        errorMessage: errorMessage,
        closeError: function () { return setErrorMessage(null); },
    };
};
